import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { NotificationBell } from "@/components/NotificationBell";
import {
  LayoutDashboard, Users, MapPin, FileText, Boxes, Wrench, Calendar,
  AlertTriangle, ReceiptText, Package, ShoppingCart, FolderKanban,
  UserCog, Truck, Banknote, FileSpreadsheet, BarChart3, Settings,
  Search, LogOut, LifeBuoy, ShieldCheck, ArrowLeft, ClipboardList,
  Repeat, Inbox
} from "lucide-react";

const NAV = [
  { key: "dashboard", label: "Dashboard", to: "/app/dashboard", icon: LayoutDashboard },
  { key: "customers", label: "Customers", to: "/app/customers", icon: Users },
  { key: "sites", label: "Sites", to: "/app/sites", icon: MapPin },
  { key: "contracts", label: "Contract Margins", to: "/app/contracts", icon: FileText },
  { key: "templates", label: "Recurring Works", to: "/app/job-templates", icon: Repeat },
  { key: "projects", label: "Projects", to: "/app/projects", icon: FolderKanban },
  { key: "unscheduled", label: "Unscheduled Work", to: "/app/unscheduled", icon: Inbox },
  { key: "schedule", label: "Schedule", to: "/app/schedule", icon: Calendar },
  { key: "jobs", label: "Jobs", to: "/app/jobs", icon: Wrench },
  { key: "assets", label: "Assets", to: "/app/assets", icon: Boxes },
  { key: "defects", label: "Defects", to: "/app/defects", icon: AlertTriangle },
  { key: "quotations", label: "Quotations", to: "/app/quotations", icon: ReceiptText },
  { key: "stock", label: "Stock", to: "/app/stock", icon: Package },
  { key: "purchasing", label: "Purchasing", to: "/app/purchase-orders", icon: ShoppingCart },
  { key: "people", label: "People", to: "/app/employees", icon: UserCog },
  { key: "vehicles", label: "Vehicles & Equipment", to: "/app/vehicles", icon: Truck },
  { key: "billing", label: "Billing", to: "/app/billing", icon: Banknote },
  { key: "reports", label: "Reports", to: "/app/reports", icon: BarChart3 },
  { key: "documents", label: "Audit Trail", to: "/app/audit", icon: ClipboardList },
  { key: "settings", label: "Settings", to: "/app/settings", icon: Settings },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { company } = useTenant();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const isAmden = user?.role === "amden_super_admin";

  const allowedKeys = company?.nav || [];
  const items = NAV.filter((n) => allowedKeys.includes(n.key));

  const search = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    const { data } = await api.get("/search", { params: { q: val } });
    setResults(data);
  };

  const go = (r) => {
    setResults([]); setQ("");
    const map = { customers: "customers", sites: "sites", jobs: "jobs", assets: "assets", defects: "defects", quotations: "quotations", stock: "stock" };
    nav(`/app/${map[r.type] || r.type}?open=${r.id}`);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Amden sidebar */}
      <aside className="w-64 bg-[#0F1725] text-slate-200 flex flex-col fixed inset-y-0 z-20">
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
          <img src="/brand/dispatch-logo.png" alt="Amden Dispatch" className="h-8 w-8 rounded-md" />
          <div><div className="font-head font-bold text-white leading-none">Amden Dispatch</div>
            <div className="text-[10px] text-slate-400">Managed Dashboard</div></div>
        </div>
        <nav className="flex-1 overflow-y-auto scroll-thin py-3 px-2">
          {items.map((n) => (
            <NavLink key={n.key} to={n.to} data-testid={`nav-${n.key}`}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm mb-0.5 transition-colors ${isActive ? "bg-white/10 text-white font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-3 border-t border-white/10 text-xs text-slate-500">
          <a href="mailto:support@amden.io" className="flex items-center gap-2 hover:text-slate-300 transition-colors mb-1"><LifeBuoy className="h-3.5 w-3.5" /> Amden Support</a>
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Powered by Amden Dispatch</span>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        {/* Client topbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6 gap-4 sticky top-0 z-10">
          {isAmden && (
            <button data-testid="back-to-admin" onClick={() => nav("/admin")} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm transition-colors">
              <ArrowLeft className="h-4 w-4" /> Admin
            </button>
          )}
          <div className="flex items-center gap-2.5">
            {company?.logo ? (
              <img data-testid="topbar-company-logo" src={`${API}/public/companies/${company.id}/logo?v=${company.logo_version || 0}`} alt={company?.name} className="h-8 max-w-[130px] object-contain" />
            ) : (
              <div className="h-8 w-8 rounded-md grid place-items-center text-white font-head font-bold" style={{ backgroundColor: "var(--client-accent)" }}>
                {(company?.name || "?")[0]}
              </div>
            )}
            <div>
              <div className="font-head font-bold text-slate-900 leading-none">{company?.name || "Workspace"}</div>
              <div className="text-[11px] text-slate-400">{company?.trading_name}{isAmden && " · Amden support session"}</div>
            </div>
          </div>

          <div className="relative flex-1 max-w-md ml-4">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="global-search" placeholder="Search customers, jobs, assets…" value={q} onChange={(e) => search(e.target.value)} className="pl-8 bg-slate-50" />
            {results.length > 0 && (
              <div className="absolute mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg z-30 max-h-72 overflow-y-auto">
                {results.map((r) => (
                  <button key={`${r.type}-${r.id}`} onClick={() => go(r)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0">
                    <span className="text-slate-400 text-xs uppercase mr-2">{r.type}</span>{r.ref} · {r.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-4">
            <NotificationBell />
            <div className="text-right">
              <div className="text-sm font-medium text-slate-800">{user?.name}</div>
              <div className="text-[11px] text-slate-400">{user?.role_label}</div>
            </div>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="text-slate-400 hover:text-red-600 transition-colors">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>

        <main className="p-6 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
